/**
 * SB6-ANA-CACHE-03 — per-dataset cache manifest (IndexedDB).
 *
 * The Cache Storage bucket (`wp-images-v1`, CACHE-01) holds the image *bytes*
 * keyed by canonical blob path. It has no notion of which dataset an image
 * belongs to, how big the dataset is, when it was last touched, or whether the
 * user pinned it. This module is that bookkeeping layer: a small IndexedDB
 * database mapping each data-acquisition to the set of its cached images plus
 * the metadata the storage UI and the LRU evictor need.
 *
 * ## Record shape (one per acquisition)
 * - `dataAcquisitionId`     — primary key.
 * - `imageKeys`             — canonical keys of the images of this dataset whose
 *                             full-res bytes are in the cache.
 * - `totalBytes`            — sum of cached image sizes (Content-Length / blob.size).
 * - `lastAccess`            — epoch ms, touched on cache reads (throttled).
 * - `pinned`                — LRU-exempt flag.
 * - `totalImageCount`       — denominator for the completeness readout ("312/369").
 * - `name`                  — human label for the storage UI (best-effort).
 *
 * ## Design constraints
 * - **Pluggable backend.** Real browsers use IndexedDB; jsdom unit tests inject
 *   an in-memory backend via `__setBackend`. Nothing else in the module touches
 *   `indexedDB` directly.
 * - **Synchronous mirror.** An in-memory `Map` mirror is kept in lock-step with
 *   the backend so the React surface (`useCacheManifest`) and the LRU evictor can
 *   read the current state synchronously; writes are async (fire-and-forget into
 *   IDB) but update the mirror + notify subscribers immediately.
 * - **Never throws.** Every IDB interaction is wrapped; a failure degrades to the
 *   mirror-only path (cache still works, bookkeeping is best-effort) — exactly
 *   like `imageCache` degrading when Cache Storage is unavailable.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DatasetManifest {
  /** Data-acquisition id (primary key). */
  dataAcquisitionId: string;
  /** Canonical keys of cached full-res images of this dataset. */
  imageKeys: string[];
  /** Sum of cached image byte sizes. */
  totalBytes: number;
  /** Epoch ms of the last read/open touch (LRU recency). */
  lastAccess: number;
  /** LRU-exempt flag. */
  pinned: boolean;
  /** Total images in the dataset (completeness denominator). */
  totalImageCount: number;
  /** Human-readable dataset label for the storage UI (best-effort). */
  name?: string;
}

/** The minimal async persistence surface the manifest needs. */
export interface ManifestBackend {
  loadAll(): Promise<DatasetManifest[]>;
  put(record: DatasetManifest): Promise<void>;
  delete(id: string): Promise<void>;
  clear(): Promise<void>;
}

// ---------------------------------------------------------------------------
// IndexedDB backend
// ---------------------------------------------------------------------------

const DB_NAME = "wp-image-cache-manifest";
const STORE = "datasets";
const DB_VERSION = 1;

function idbAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "dataAcquisitionId" });
      }
    };
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error ?? new Error("indexedDB.open failed"));
    };
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      resolve();
    };
    tx.onerror = () => {
      reject(tx.error ?? new Error("transaction failed"));
    };
    tx.onabort = () => {
      reject(tx.error ?? new Error("transaction aborted"));
    };
  });
}

/** The real IndexedDB-backed manifest store. */
const idbBackend: ManifestBackend = {
  async loadAll() {
    if (!idbAvailable()) return [];
    const db = await openDb();
    try {
      const tx = db.transaction(STORE, "readonly");
      const store = tx.objectStore(STORE);
      const all = await new Promise<DatasetManifest[]>((resolve, reject) => {
        const req = store.getAll();
        req.onsuccess = () => {
          resolve(req.result as DatasetManifest[]);
        };
        req.onerror = () => {
          reject(req.error ?? new Error("getAll failed"));
        };
      });
      return all;
    } finally {
      db.close();
    }
  },
  async put(record) {
    if (!idbAvailable()) return;
    const db = await openDb();
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(record);
      await txDone(tx);
    } finally {
      db.close();
    }
  },
  async delete(id) {
    if (!idbAvailable()) return;
    const db = await openDb();
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(id);
      await txDone(tx);
    } finally {
      db.close();
    }
  },
  async clear() {
    if (!idbAvailable()) return;
    const db = await openDb();
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).clear();
      await txDone(tx);
    } finally {
      db.close();
    }
  },
};

// ---------------------------------------------------------------------------
// Store — in-memory mirror + subscribable surface
// ---------------------------------------------------------------------------

/** Throttle window for `lastAccess` touches (avoid an IDB write per read). */
const TOUCH_THROTTLE_MS = 30_000;

class CacheManifestStore {
  private backend: ManifestBackend = idbBackend;
  /** Synchronous mirror: dataAcquisitionId → manifest. */
  private mirror = new Map<string, DatasetManifest>();
  /** Per-dataset set of cached keys, for O(1) membership + dedupe. */
  private keySets = new Map<string, Set<string>>();
  /**
   * Datasets whose PIN flag was explicitly set this session. Hydration must NOT
   * let a stale persisted snapshot override an explicit pin/unpin that raced the
   * initial `loadAll()` — e.g. if the user unpins while hydration is in flight,
   * the resolving hydrate must not re-apply the persisted `pinned:true`. Only
   * the pin field is protected; cached keys / bytes / recency still merge so the
   * reload-restore path (registerDataset placeholder → adopt persisted state)
   * keeps working.
   */
  private pinDirty = new Set<string>();
  /** Last epoch ms we persisted a touch per dataset (throttle). */
  private lastTouchPersist = new Map<string, number>();
  private listeners = new Set<() => void>();
  private snapshot: DatasetManifest[] = [];
  private hydrated = false;
  private hydrating: Promise<void> | null = null;
  /**
   * Serialised backend-write chain. All IDB mutations (put/delete/clear) run in
   * submission order so a `clearAll` followed by a re-seed can never be clobbered
   * by a late-committing clear. `flush()` awaits the tail (used by tests + any
   * caller that needs durability before a navigation).
   */
  private writeChain: Promise<void> = Promise.resolve();

  private enqueueWrite(op: () => Promise<void>): void {
    this.writeChain = this.writeChain.then(op, op);
  }

  /** Await all pending backend writes (durability barrier). */
  flush(): Promise<void> {
    return this.writeChain;
  }

  // -- store surface ---------------------------------------------------------

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  /** Stable snapshot array (referentially stable until a mutation). */
  getSnapshot = (): DatasetManifest[] => this.snapshot;

  private emit(): void {
    // Rebuild the immutable snapshot once, then notify.
    this.snapshot = [...this.mirror.values()].map((m) => ({
      ...m,
      imageKeys: m.imageKeys,
    }));
    for (const fn of this.listeners) fn();
  }

  // -- hydration -------------------------------------------------------------

  /**
   * Load the persisted manifest into the mirror. Idempotent; concurrent callers
   * share the same in-flight promise. The provider calls this once on mount.
   */
  hydrate(): Promise<void> {
    if (this.hydrated) return Promise.resolve();
    if (this.hydrating) return this.hydrating;
    this.hydrating = (async () => {
      try {
        const records = await this.backend.loadAll();
        for (const rec of records) {
          const live = this.mirror.get(rec.dataAcquisitionId);
          if (!live) {
            this.mirror.set(rec.dataAcquisitionId, rec);
            this.keySets.set(rec.dataAcquisitionId, new Set(rec.imageKeys));
            continue;
          }
          // A placeholder was created before hydration finished (e.g. the open
          // dataset's `registerDataset` ran first). MERGE the persisted state
          // into it — adopting the persisted cached keys / bytes (the
          // load-bearing Friday→Monday state) while keeping the freshest live
          // `totalImageCount` + `name`. Live cache records that landed during
          // hydration are unioned in, not lost.
          const mergedKeys = new Set([...rec.imageKeys, ...live.imageKeys]);
          live.imageKeys = [...mergedKeys];
          live.totalBytes = Math.max(rec.totalBytes, live.totalBytes);
          live.lastAccess = Math.max(rec.lastAccess, live.lastAccess);
          live.totalImageCount = Math.max(
            rec.totalImageCount,
            live.totalImageCount,
          );
          if (!live.name && rec.name) live.name = rec.name;
          // The PIN flag: if it was explicitly set this session (pinDirty), the
          // live value wins (don't re-pin a just-unpinned dataset). Otherwise —
          // the common reload-restore path — adopt the persisted pin.
          if (!this.pinDirty.has(rec.dataAcquisitionId)) {
            live.pinned = rec.pinned;
          }
          this.keySets.set(rec.dataAcquisitionId, mergedKeys);
          this.persist(rec.dataAcquisitionId);
        }
      } catch {
        /* degrade to mirror-only */
      } finally {
        this.hydrated = true;
        this.hydrating = null;
        this.emit();
      }
    })();
    return this.hydrating;
  }

  // -- reads -----------------------------------------------------------------

  get(id: string): DatasetManifest | undefined {
    return this.mirror.get(id);
  }

  list(): DatasetManifest[] {
    return [...this.mirror.values()];
  }

  /** Total bytes of UNPINNED datasets (what the LRU budget caps). */
  unpinnedBytes(): number {
    let sum = 0;
    for (const m of this.mirror.values()) if (!m.pinned) sum += m.totalBytes;
    return sum;
  }

  /** Number of UNPINNED datasets (what the count budget caps). */
  unpinnedCount(): number {
    let n = 0;
    for (const m of this.mirror.values()) if (!m.pinned) n += 1;
    return n;
  }

  // -- writes ----------------------------------------------------------------

  private persist(id: string): void {
    const rec = this.mirror.get(id);
    if (!rec) return;
    const snapshot = { ...rec, imageKeys: [...rec.imageKeys] };
    this.enqueueWrite(() => this.backend.put(snapshot));
  }

  private ensure(id: string): DatasetManifest {
    let rec = this.mirror.get(id);
    if (!rec) {
      rec = {
        dataAcquisitionId: id,
        imageKeys: [],
        totalBytes: 0,
        lastAccess: Date.now(),
        pinned: false,
        totalImageCount: 0,
      };
      this.mirror.set(id, rec);
      this.keySets.set(id, new Set());
    }
    return rec;
  }

  /** Set/refresh the dataset's name + total image count (on open). */
  registerDataset(id: string, totalImageCount: number, name?: string): void {
    if (!id) return;
    const rec = this.ensure(id);
    let changed = false;
    if (totalImageCount > 0 && rec.totalImageCount !== totalImageCount) {
      rec.totalImageCount = totalImageCount;
      changed = true;
    }
    if (name && rec.name !== name) {
      rec.name = name;
      changed = true;
    }
    if (changed) {
      this.persist(id);
      this.emit();
    }
  }

  /**
   * Record that an image's full-res bytes were cached. Idempotent per key — a
   * re-record of an already-known key only refreshes recency, never double-counts
   * bytes. `bytes` should be the response Content-Length or blob.size.
   */
  recordCachedImage(id: string, key: string, bytes: number): void {
    if (!id || !key) return;
    const rec = this.ensure(id);
    const keys = this.keySets.get(id);
    if (!keys) return;
    rec.lastAccess = Date.now();
    if (keys.has(key)) {
      // Already counted — just a recency touch.
      this.persist(id);
      this.emit();
      return;
    }
    keys.add(key);
    rec.imageKeys = [...keys];
    rec.totalBytes += Math.max(0, bytes);
    this.persist(id);
    this.emit();
  }

  /**
   * Touch a dataset's recency on a cache READ. Throttled so a burst of reads
   * (image stepping) doesn't thrash IndexedDB — the mirror is updated every
   * call (cheap, keeps in-memory LRU honest) but only persisted every
   * `TOUCH_THROTTLE_MS`.
   */
  touch(id: string): void {
    if (!id) return;
    const rec = this.mirror.get(id);
    if (!rec) return;
    const now = Date.now();
    rec.lastAccess = now;
    const last = this.lastTouchPersist.get(id) ?? 0;
    if (now - last >= TOUCH_THROTTLE_MS) {
      this.lastTouchPersist.set(id, now);
      this.persist(id);
    }
    // No emit on a throttled touch — recency changes don't move the UI; the LRU
    // reads the mirror directly. (Pin/clear/byte changes do emit.)
  }

  /**
   * Set the pinned flag (immediate persist + notify). Pinning/unpinning does NOT
   * touch `lastAccess` — unpinning an old dataset must leave it the LRU victim
   * (it re-enters the budget at its real recency, AC3), and pinning shouldn't
   * shuffle recency either.
   */
  setPinned(id: string, pinned: boolean): void {
    if (!id) return;
    const rec = this.ensure(id);
    // Mark pin-dirty even if the value is unchanged: an explicit pin/unpin this
    // session must win over a racing hydration regardless of the prior value.
    this.pinDirty.add(id);
    if (rec.pinned === pinned) return;
    rec.pinned = pinned;
    this.persist(id);
    this.emit();
  }

  /** Remove a dataset's manifest entry entirely (cache keys cleared elsewhere). */
  removeDataset(id: string): void {
    if (!this.mirror.has(id)) return;
    this.mirror.delete(id);
    this.keySets.delete(id);
    this.lastTouchPersist.delete(id);
    this.pinDirty.delete(id);
    this.enqueueWrite(() => this.backend.delete(id));
    this.emit();
  }

  /** Drop every manifest entry (paired with imageCache.clearAll). */
  clearAll(): void {
    this.mirror.clear();
    this.keySets.clear();
    this.lastTouchPersist.clear();
    this.pinDirty.clear();
    this.enqueueWrite(() => this.backend.clear());
    this.emit();
  }

  // -- test/diagnostic surface ----------------------------------------------

  /** @internal — force-persist a record (tests seed a forced lastAccess). */
  __persistNow(id: string): void {
    this.persist(id);
    this.emit();
  }

  /** @internal — swap the persistence backend (tests). Resets all state. */
  __setBackend(backend: ManifestBackend): void {
    this.backend = backend;
    this.mirror.clear();
    this.keySets.clear();
    this.lastTouchPersist.clear();
    this.pinDirty.clear();
    this.writeChain = Promise.resolve();
    this.hydrated = false;
    this.hydrating = null;
    this.snapshot = [];
  }

  /** @internal — force the IDB backend back (test teardown). */
  __resetToIdb(): void {
    this.__setBackend(idbBackend);
  }
}

export const cacheManifest = new CacheManifestStore();

// ---------------------------------------------------------------------------
// Acquisition-id derivation
// ---------------------------------------------------------------------------

/**
 * Derive the data-acquisition id from a canonical cache key.
 *
 * Canonical keys are blob paths of the form
 *   `<container>/data-acquisitions/<uuid>/<kind>/<file>`   (Azure)
 *   `test_data/<uuid>/<kind>/<file>`                       (dev-api)
 *
 * The acquisition UUID is the segment after `data-acquisitions/`, or — for the
 * dev-api shape — the first UUID-looking segment. Returns `null` when no
 * acquisition id can be recovered (the caller then skips manifest bookkeeping
 * for that key rather than mis-attributing it).
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function acquisitionIdFromKey(key: string): string | null {
  if (!key) return null;
  const segments = key.split("/").filter(Boolean);
  const daIdx = segments.indexOf("data-acquisitions");
  if (daIdx >= 0 && daIdx + 1 < segments.length) {
    return segments[daIdx + 1] ?? null;
  }
  // Fallback: the first UUID-looking segment (dev-api `test_data/<uuid>/…`).
  for (const seg of segments) {
    if (UUID_RE.test(seg)) return seg;
  }
  return null;
}
